import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id, Doc } from "../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";
import { MapPin, Building2 } from "lucide-react";
import { ServiceConflictDialog } from "./ServiceConflictDialog";

interface AttachToLocationSheetProps {
  isOpen: boolean;
  onClose: () => void;
  bidId: Id<"bids">;
  businessId: Id<"businesses">;
  currentLocationId?: Id<"serviceLocations">;
  onSuccess?: () => void;
}

export function AttachToLocationSheet({
  isOpen,
  onClose,
  bidId,
  businessId,
  currentLocationId,
  onSuccess,
}: AttachToLocationSheetProps) {
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);
  const [pendingLocationId, setPendingLocationId] = useState<string>("");
  const { toast } = useToast();

  // Fetch locations for this business
  const locations = useQuery(api.serviceLocations.getLocationsByBusiness, { businessId }) ?? [];

  // Check if selected location has manual services
  const hasManualServices = useQuery(
    api.serviceLocations.hasManualServices,
    selectedLocationId ? { locationId: selectedLocationId as Id<"serviceLocations"> } : "skip"
  );

  // Get manual services count for the dialog
  const manualServices =
    useQuery(
      api.serviceLocations.getLocationServices,
      selectedLocationId ? { locationId: selectedLocationId as Id<"serviceLocations"> } : "skip"
    ) ?? [];

  // Mutations
  const updateBid = useMutation(api.bids.update);
  const deleteAllServices = useMutation(api.serviceLocations.deleteAllLocationServices);

  // Reset form when sheet opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedLocationId(currentLocationId || "");
    } else {
      setSelectedLocationId("");
      setIsSubmitting(false);
      setIsConflictDialogOpen(false);
      setPendingLocationId("");
    }
  }, [isOpen, currentLocationId]);

  // Handle submit - check for conflicts first
  const handleSubmit = async () => {
    if (!selectedLocationId) {
      toast({
        title: "Selection required",
        description: "Please select a location",
        variant: "destructive",
      });
      return;
    }

    // Check if location has existing manual services
    if (hasManualServices) {
      setPendingLocationId(selectedLocationId);
      setIsConflictDialogOpen(true);
      return;
    }

    // No conflicts, proceed with attachment
    await attachBidToLocation(selectedLocationId);
  };

  // Attach bid to location (actual attachment logic)
  const attachBidToLocation = async (locationId: string) => {
    setIsSubmitting(true);

    try {
      await updateBid({
        id: bidId,
        serviceLocationId: locationId as Id<"serviceLocations">,
      });

      toast({
        title: "Success!",
        description: "Bid successfully attached to location!",
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error attaching bid to location:", error);
      toast({
        title: "Error",
        description: "Failed to attach bid to location. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle "Keep Both" option
  const handleKeepBoth = async () => {
    setIsConflictDialogOpen(false);
    await attachBidToLocation(pendingLocationId);
  };

  // Handle "Replace" option - delete manual services first
  const handleReplace = async () => {
    setIsConflictDialogOpen(false);
    setIsSubmitting(true);

    try {
      // Delete all manual services for this location
      await deleteAllServices({ locationId: pendingLocationId as Id<"serviceLocations"> });

      // Then attach the bid
      await attachBidToLocation(pendingLocationId);
    } catch (error) {
      console.error("Error replacing services:", error);
      toast({
        title: "Error",
        description: "Failed to replace services. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const handleDetach = async () => {
    setIsSubmitting(true);

    try {
      await updateBid({
        id: bidId,
        serviceLocationId: undefined,
      });

      toast({
        title: "Success!",
        description: "Bid detached from location!",
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error detaching bid from location:", error);
      toast({
        title: "Error",
        description: "Failed to detach bid from location. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get location name for conflict dialog
  const getLocationName = () => {
    const location = locations.find((l: any) => l._id === pendingLocationId);
    return location?.name || "this location";
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Attach Bid to Location
            </SheetTitle>
            <SheetDescription>
              Select a service location to attach this bid to. This will help organize bids by
              specific client locations.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {locations.length === 0 ? (
              <div className="py-8 text-center">
                <Building2 className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                <h3 className="mb-2 text-lg font-medium text-gray-900">No Locations Found</h3>
                <p className="text-gray-500">
                  This client doesn't have any service locations yet. Create a location first to
                  attach bids to it.
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="location">Service Location</Label>
                  <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a location..." />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((location: Doc<"serviceLocations">) => (
                        <SelectItem key={location._id} value={location._id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{location.name}</span>
                            <span className="text-sm text-gray-500">{location.address}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {currentLocationId && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Current Location:</strong>{" "}
                      {locations.find((l: Doc<"serviceLocations">) => l._id === currentLocationId)
                        ?.name || "Unknown Location"}
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !selectedLocationId}
                    className="flex-1"
                  >
                    {isSubmitting ? "Attaching..." : "Attach to Location"}
                  </Button>

                  {currentLocationId && (
                    <Button variant="outline" onClick={handleDetach} disabled={isSubmitting}>
                      {isSubmitting ? "Detaching..." : "Detach"}
                    </Button>
                  )}
                </div>
              </>
            )}

            <Button variant="outline" onClick={onClose} className="w-full">
              Cancel
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Conflict Dialog */}
      <ServiceConflictDialog
        isOpen={isConflictDialogOpen}
        onClose={() => setIsConflictDialogOpen(false)}
        onKeepBoth={handleKeepBoth}
        onReplace={handleReplace}
        locationName={getLocationName()}
        serviceCount={manualServices.length}
      />
    </>
  );
}
